import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface FooterLink {
  label: string;
  href: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerSections: FooterSection[] = [
    {
      title: 'Capabilities',
      links: [
        { label: 'CNC Machining', href: '/capabilities' },
        { label: 'Additive Manufacturing', href: '/capabilities' },
        { label: 'Sheet Metal', href: '/capabilities' },
        { label: 'Composites', href: '/capabilities' },
      ],
    },
    {
      title: 'Resources',
      links: [
        { label: 'Technical Guides', href: '/resources' },
        { label: 'Material Specs', href: '/resources' },
        { label: 'Design Guidelines', href: '/resources' },
        { label: 'Compliance Docs', href: '/resources' },
      ],
    },
    {
      title: 'Company',
      links: [
        { label: 'About Techaero', href: '/about' },
        { label: 'Mission Support', href: '/support' },
        { label: 'Client Portal', href: '/client-portal' },
        { label: 'Certifications', href: '/about' },
      ],
    },
  ];

  const socialLinks = [
    { name: 'LinkedIn', icon: 'LinkIcon', href: '#' },
    { name: 'Twitter', icon: 'LinkIcon', href: '#' },
    { name: 'YouTube', icon: 'LinkIcon', href: '#' },
  ];

  const certifications = [
    { name: 'AS9100', icon: 'ShieldCheckIcon' },
    { name: 'ITAR', icon: 'LockClosedIcon' },
    { name: 'NADCAP', icon: 'CheckBadgeIcon' },
    { name: 'ISO 9001', icon: 'DocumentCheckIcon' },
  ];

  return (
    <footer className="relative bg-gradient-to-b from-primary to-background border-t border-border">
      <div className="mx-auto px-6 lg:px-8 py-16">
        {/* Main Footer Content */}
        <div className="grid lg:grid-cols-5 gap-12 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-2 space-y-6">
            <Link href="/homepage" className="inline-flex items-center space-x-3 group">
              <div className="w-12 h-12 flex items-center justify-center transform transition-transform duration-300 group-hover:scale-110">
                <img
                  src="/assets/images/JOURNAL-1782625187518.png"
                  alt="Wings Bharath Logo"
                  className="w-12 h-12 object-contain"
                />
              </div>
              <div className="flex flex-col">
                <span className="font-accent text-xl font-bold text-foreground tracking-wide">
                  TECHAERO
                </span>
                <span className="font-cta text-xs text-technical-gray tracking-wider">
                  MISSION CONTROL
                </span>
              </div>
            </Link>

            <p className="font-body text-technical-gray leading-relaxed max-w-md">
              Mission-critical aerospace manufacturing excellence. Precision-engineered components for satellites, launch vehicles, and defense systems.
            </p>

            {/* Certifications */}
            <div className="flex flex-wrap gap-3">
              {certifications.map((cert) => (
                <div
                  key={cert.name}
                  className="flex items-center space-x-2 px-3 py-2 bg-muted rounded-lg border border-border hover:border-aerospace-green transition-colors duration-300"
                >
                  <Icon name={cert.icon as any} size={16} className="text-aerospace-green" />
                  <span className="font-cta text-xs text-technical-gray">{cert.name}</span>
                </div>
              ))}
            </div>

            {/* Social Links */}
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center hover:bg-accent/20 hover:border-accent border border-border transition-all duration-300"
                  aria-label={social.name}
                >
                  <Icon name={social.icon as any} size={20} className="text-technical-gray hover:text-accent" />
                </a>
              ))}
            </div>
          </div>

          {/* Footer Links */}
          {footerSections.map((section) => (
            <div key={section.title}>
              <h3 className="font-headline text-sm font-bold text-foreground uppercase tracking-wider mb-4">
                {section.title}
              </h3>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      href={link.href}
                      className="font-body text-sm text-technical-gray hover:text-accent transition-colors duration-300"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="font-body text-sm text-technical-gray">
              © {currentYear} Techaero Platform. All rights reserved. Mission-Critical Manufacturing Excellence.
            </p>
            <div className="flex space-x-6">
              <Link
                href="#"
                className="font-body text-sm text-technical-gray hover:text-accent transition-colors duration-300"
              >
                Privacy Policy
              </Link>
              <Link
                href="#"
                className="font-body text-sm text-technical-gray hover:text-accent transition-colors duration-300"
              >
                Terms of Service
              </Link>
              <Link
                href="#"
                className="font-body text-sm text-technical-gray hover:text-accent transition-colors duration-300"
              >
                Security
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;